import { useCallback, useRef } from "react";
import type { ReactNode } from "react";
import styles from "./Dock.module.css";
import { useEventListener } from "@web-monorepo/hooks";

type DockPosition = "left" | "right" | "bottom";

interface DockProps {
  position: DockPosition;
  fluid?: boolean;
  size: number;
  onSizeChange?: (size: number) => void;
  isVisible: boolean;
  zIndex: number;
  children: ReactNode;
}

export function Dock({ position, fluid, size, onSizeChange, isVisible, zIndex, children }: DockProps) {
  const dragStartRef = useRef<{
    pageX: number;
    pageY: number;
    startSize: number;
  } | null>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!onSizeChange) return;
      dragStartRef.current = {
        pageX: e.pageX,
        pageY: e.pageY,
        startSize: size,
      };
      e.preventDefault();
    },
    [onSizeChange, size],
  );

  useEventListener(
    "mousemove",
    (e: MouseEvent) => {
      const start = dragStartRef.current;
      if (!start) return;
      let next = start.startSize;
      if (position === "left") next = start.startSize + (e.pageX - start.pageX);
      else if (position === "right") next = start.startSize - (e.pageX - start.pageX);
      else if (position === "bottom") next = start.startSize - (e.pageY - start.pageY);
      onSizeChange?.(next);
    },
    globalThis.window,
  );

  useEventListener(
    "mouseup",
    () => {
      dragStartRef.current = null;
    },
    globalThis.window,
  );

  const isHorizontal = position === "left" || position === "right";
  const dimension = fluid ? "100%" : `${size}px`;

  const dockStyle: React.CSSProperties = {
    "--dock-z": zIndex,
    "--dock-size": dimension,
  } as React.CSSProperties;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!onSizeChange) return;
      const delta = e.shiftKey ? 50 : 10;
      const direction = position === "left" ? 1 : -1;
      if (isHorizontal && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
        e.preventDefault();
        onSizeChange(size + direction * (e.key === "ArrowRight" ? delta : -delta));
      } else if (!isHorizontal && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
        e.preventDefault();
        onSizeChange(size + (e.key === "ArrowUp" ? delta : -delta));
      }
    },
    [onSizeChange, position, isHorizontal, size],
  );

  return (
    <div className={styles.dock} data-position={position} data-visible={String(isVisible)} style={dockStyle}>
      {onSizeChange && !fluid && (
        <div
          className={styles.resizeHandle}
          data-orientation={isHorizontal ? "horizontal" : "vertical"}
          onMouseDown={handleMouseDown}
          onKeyDown={handleKeyDown}
          role="slider"
          tabIndex={0}
          aria-orientation={isHorizontal ? "horizontal" : "vertical"}
          aria-label="Resize panel"
          aria-valuenow={size}
          aria-valuemin={100}
          aria-valuemax={2000}
        />
      )}
      {children}
    </div>
  );
}
